/**
 * CalendarV2 Component
 * Main container for the Calendar V2 module
 * Integrates all components with context
 */

import React from 'react';
import { CalendarProvider, useCalendarState } from './context/CalendarContext';
import { useCalendarUI } from './context/CalendarUIContext';

// Components
import CalendarHeader from './components/CalendarHeader/CalendarHeader';
import FilterSection from './components/CalendarHeader/FilterSection';
import CalendarView from './components/CalendarView/CalendarView';
import UserSidebar from './components/Sidebar/UserSidebar';
import CalendarSettings from './components/Settings/CalendarSettings';
import EventViewModal from './components/EventModals/EventViewModal';
import EventFormModal from './components/EventModals/EventFormModal';
import DayEventsPopup from './components/DayEventsPopup/DayEventsPopup';

// Styles
import './styles/calendarv2.scss';

// Inner content component that can access context
const CalendarV2Content = () => {
  const { sidebarCollapsed, toggleSidebar } = useCalendarUI();
  const { isGlobal } = useCalendarState();

  return (
    <div className="calendar-v2-container">
      {/* Header */}
      {isGlobal && <CalendarHeader />}

      {/* Filters */}
      <FilterSection />

      {/* Mobile sidebar and backdrop - only for <992px */}
      {isGlobal && (
        <>
          {/* Backdrop */}
          {!sidebarCollapsed && (
            <div
              className="mobile-sidebar-backdrop d-block d-lg-none"
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                background: 'rgba(0,0,0,0.25)',
                zIndex: 1049,
                pointerEvents: 'auto',
              }}
              onClick={toggleSidebar}
            />
          )}
          {/* Sidebar: only render when open on mobile */}
          {!sidebarCollapsed && (
            <div
              className="calendar-sidebar-mobile d-block d-lg-none"
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '90vw',
                height: '100%',
                zIndex: 1050,
                background: 'var(--background)',
                boxShadow: '2px 0 8px rgba(0,0,0,0.08)',
                transition: 'left 0.3s cubic-bezier(.4,0,.2,1)',
                overflowY: 'auto',
                maxWidth: '90vw',
                display: 'block',
                paddingLeft: 14,
              }}
            >
              <button
                type="button"
                className="btn btn-link calendar-sidebar-close position-absolute top-0 end-0 m-2"
                style={{ fontSize: 24, zIndex: 1060 }}
                aria-label="Close sidebar"
                onClick={toggleSidebar}
              >
                <span aria-hidden="true">&times;</span>
              </button>
              <UserSidebar />
            </div>
          )}
        </>
      )}

      {/* Main Content */}
      <div
        className="calendar-v2-main"
        style={
          !sidebarCollapsed && window.innerWidth < 992 ? { pointerEvents: 'none', userSelect: 'none', filter: 'blur(1px)' } : undefined
        }
      >
        {/* Desktop sidebar: always visible on >=992px */}
        {isGlobal && (
          <div
            className={`d-none d-lg-block calendar-sidebar${sidebarCollapsed ? ' collapsed' : ' expanded'}`}
            style={{
              width: sidebarCollapsed ? 56 : 300,
              minWidth: sidebarCollapsed ? 56 : 220,
              maxWidth: sidebarCollapsed ? 56 : 350,
              height: '100%',
              transition:
                'width 0.3s cubic-bezier(.4,0,.2,1), min-width 0.3s cubic-bezier(.4,0,.2,1), max-width 0.3s cubic-bezier(.4,0,.2,1)',
              overflow: 'hidden',
            }}
          >
            <UserSidebar />
          </div>
        )}
        {/* Calendar */}
        <CalendarView />
      </div>

      {/* Modals */}
      <CalendarSettings />
      <EventViewModal />
      <EventFormModal />

      {/* Day Events Popup */}
      <DayEventsPopup />
    </div>
  );
};

const CalendarV2 = ({ ...props }) => {
  return (
    <CalendarProvider {...props}>
      <CalendarV2Content />
    </CalendarProvider>
  );
};

export default CalendarV2;
